// The small functions every legacy list and tile carried its own copy of —
// getIcon, getTitle, getUntilOrSinceDate, formatTimeInformation — once, and
// read from the portal's descriptors instead of from five booleans.

import type { FormDescriptor } from "~/api";
import { modeOf } from "~/components/clock/clock";
import { durationText, fmtDate, fmtTime, parse, sameDay, viennaNow, type Mode } from "~/components/clock/dates";
import CalendarIcon from "./icons/CalendarIcon";
import CalendarNoEndIcon from "./icons/CalendarNoEndIcon";
import DateTimeIcon from "./icons/DateTimeIcon";
import TimeIcon from "./icons/TimeIcon";

type T = (key: string, arg?: string | Record<string, string | number>) => string;

/** titleKey is the legacy getTitle(): the kind of announcement, by its dates. */
export function titleKey(mode: Mode): string {
  switch (mode) {
    case "time":
      return "time-announcement";
    case "datetime":
      return "datetime-announcement";
    case "date":
      return "date-announcement";
    default:
      return "without-enddate";
  }
}

/** modeFromIcon is the fallback when the descriptor is no longer offered. */
export function modeFromIcon(icon?: string): Mode {
  switch (icon) {
    case "calendar-open":
      return "open";
    case "datetime":
      return "datetime";
    case "timer":
      return "time";
    default:
      return "date";
  }
}

export function modeFor(d: FormDescriptor | undefined, icon?: string): Mode {
  return d ? modeOf(d) : modeFromIcon(icon);
}

/**
 * AnnouncementIcon is the legacy getIcon(): the struck-through calendar for
 * no end, the calendar with a clock for a date and time, the calendar for a
 * date or anything longer than a day, and the clock for the rest. The server
 * has already made that choice and sends it as `icon`.
 */
export function AnnouncementIcon({
  icon,
  color,
  fontSize,
  bgColor,
}: {
  icon?: string;
  color?: string;
  fontSize?: number;
  bgColor?: string;
}) {
  switch (icon) {
    case "calendar-open":
      return <CalendarNoEndIcon color={color} fontSize={fontSize} />;
    case "datetime":
      return <DateTimeIcon color={color} fontSize={fontSize} bgColor={bgColor} />;
    case "timer":
      return <TimeIcon color={color} fontSize={fontSize} />;
    default:
      return <CalendarIcon color={color} fontSize={fontSize} />;
  }
}

/** isOffice says the stand-in is an office (a secretariat) rather than a colleague. */
export function isOffice(d: FormDescriptor | undefined): boolean {
  return d?.proxy?.source === "secretariats";
}

/** hasRepresentation says the announcement names a colleague. */
export function hasRepresentation(d: FormDescriptor | undefined): boolean {
  return !!d?.proxy && d.proxy.source === "users";
}

const DAY_MS = 86_400_000;

/**
 * untilOrSince is the legacy getUntilOrSinceDate(): "Heute", "Morgen",
 * "in 3 Tagen", and for a live one "seit heute", "seit gestern",
 * "seit 3 Tagen".
 */
export function untilOrSince(startsAt: string, live: boolean, t: T): string {
  const start = parse(startsAt) ?? new Date(startsAt);
  const now = viennaNow();
  // moment's diff(…, "days", true), rounded up.
  const days = Math.ceil((start.getTime() - now.getTime()) / DAY_MS);
  if (sameDay(start, now)) return t("today");
  if (live) {
    if (days === 0) return t("since-today");
    if (days === -1) return t("since-yesterday");
    return t("since-x-days", { x: Math.abs(days) });
  }
  if (days === 1) return t("tomorrow");
  return t("in-x-days", { x: days });
}

/** stamp is "HH:mm" today and "DD.MM.YYYY HH:mm" on any other day. */
function stamp(d: Date): string {
  return sameDay(d, viennaNow()) ? fmtTime(d) : `${fmtDate(d)} ${fmtTime(d)}`;
}

/** full is always "DD.MM.YYYY HH:mm", as the expanded row shows it. */
export function full(s: string): string {
  const d = parse(s) ?? new Date(s);
  return `${fmtDate(d)} ${fmtTime(d)}`;
}

/** timeInformation is the legacy formatTimeInformation(): "08:00 - 17.09.2026 08:00 (1 Tag)". */
export function timeInformation(startsAt: string, endsAt: string | undefined, mode: Mode, t: T): string {
  const start = parse(startsAt) ?? new Date(startsAt);
  if (!endsAt) return `${stamp(start)} - ${t("no-end")}`;
  const end = parse(endsAt) ?? new Date(endsAt);
  const duration = durationText({ start, end }, mode, t);
  return `${stamp(start)} - ${stamp(end)} ${duration ? `(${duration})` : ""}`;
}

/** The repeat values the server uses, spelled with the legacy menu's words. */
export const repeatLabel: Record<string, string> = {
  none: "never",
  daily_no_weekend: "daily-without-weekend",
  daily: "daily-with-weekend",
  weekly: "weekly",
  monthly: "monthly-on-date",
  yearly: "annually",
};
